import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Bell, CheckCircle2 } from 'lucide-react'
import { useTRPC } from '@/lib/trpc'
import { formatTime } from '@/lib/format'

/**
 * What the operator watch raised for this account (John, Oct 30 2026): a
 * bell at the top right with the history behind it. New entries count on
 * the badge; opening the bell marks them seen. A resolved entry comes back
 * as new once, so the outcome is noticed too.
 */
export function NotificationBell() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const list = useQuery({ ...trpc.notification.list.queryOptions(), refetchInterval: 30_000, refetchOnWindowFocus: true })
  const markRead = useMutation(
    trpc.notification.markRead.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: trpc.notification.list.queryKey() }),
    }),
  )
  const items = list.data ?? []
  const unread = items.filter((item) => item.readAt === null).length
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
  function toggle() {
    const next = !open
    if (next && unread > 0) markRead.mutate()
    setOpen(next)
  }
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="btn btn-ghost relative !p-2"
        onClick={toggle}
        title="Notifications"
        aria-label={unread > 0 ? `Notifications, ${unread} new` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Bell className="size-5" />
        {unread > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-[#0c1a12]">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Notifications"
          className="card absolute right-0 top-full z-50 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden shadow-lg shadow-black/40"
        >
          <div className="border-b border-line px-4 py-3 font-semibold">Notifications</div>
          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted">Nothing to report.</div>
          ) : (
            <ul className="max-h-[70vh] overflow-y-auto">
              {items.map((item) => (
                <li key={item.id} className="flex gap-3 border-b border-line px-4 py-3 text-sm last:border-b-0">
                  {item.resolvedAt ? (
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-accent" />
                  ) : (
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-negative" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{item.title}</div>
                    <div className="mt-0.5 break-words text-xs text-muted">{item.detail}</div>
                    {item.resolvedAt ? (
                      <div className="mt-1 text-xs text-accent">Resolved{item.resolution ? `: ${item.resolution}` : ''}</div>
                    ) : null}
                    <div className="mt-1 text-[11px] text-muted">
                      {formatTime(item.lastAt)}
                      {item.count > 1 ? `, seen ${item.count} times` : ''}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}
